import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import config from "./config";
import PageLoader from "./PageLoader";
import InlineBanner from "./InlineBanner";
import ChatModal from "./ChatModal";
import { useTemplate } from "./TemplateContext";

// Outer panel styling for the section views. Studio gets a more premium card —
// larger radius + soft elevation + ring instead of a hard border.
const panelCls = (studio) =>
  studio
    ? "bg-white rounded-3xl shadow-xl shadow-gray-200/60 ring-1 ring-black/5 overflow-hidden"
    : "bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden";

// Premium default cover for events with no image — a vibrant live-event crowd.
const DEFAULT_EVENT_IMAGE =
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=800&q=80";

// Default artwork shown when a reward / badge has no uploaded image. Inline SVG
// data-URIs so they always render (no network dependency, never 404): a gift box
// for rewards, a gold medal-star for badges.
const DEFAULT_REWARD_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23111827'/%3E%3Cg fill='none' stroke='%239ca3af' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='120' y='192' width='160' height='118' rx='12'/%3E%3Cpath d='M112 192h176M200 192v118M200 192c-18-38-66-38-66-8 0 18 38 8 66 8zM200 192c18-38 66-38 66-8 0 18-38 8-66 8z'/%3E%3C/g%3E%3C/svg%3E";
const DEFAULT_BADGE_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23fbbf24'/%3E%3Cstop offset='1' stop-color='%23f59e0b'/%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='100' cy='100' r='72' fill='url(%23bg)'/%3E%3Cpath d='M100 58l12 25 27 3-20 19 5 27-24-13-24 13 5-27-20-19 27-3z' fill='white'/%3E%3C/svg%3E";

function Avatar({ name, img, primary, size = 44 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full overflow-hidden flex items-center justify-center font-bold shrink-0"
      style={
        img
          ? { width: size, height: size }
          : { width: size, height: size, backgroundColor: `${primary}1f`, color: primary, border: `1px solid ${primary}40`, fontSize: size * 0.4 }
      }
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : initial}
    </div>
  );
}

// Medal icon for top-3 ranks (gold / silver / bronze) — replaces the emoji.
const MedalIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 3l2.5 5M15.5 3L13 8" />
    <circle cx="12" cy="15" r="6" />
    <path d="M12 12.6l.9 1.85 2.04.3-1.47 1.43.35 2.03L12 17.3l-1.82.96.35-2.03-1.47-1.43 2.04-.3.9-1.85z" fill="currentColor" stroke="none" />
  </svg>
);

// "2026-04-12 06:31:52" -> "Apr 2026"
function fmtAchieved(d) {
  if (!d) return "";
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt)) return "";
  return dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// Small trophy glyph used next to achievement dates.
const TrophyMini = ({ className = "w-3 h-3" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
  </svg>
);

// Small padlock glyph for not-yet-reached levels.
const LockMini = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V8a5 5 0 0110 0v3M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
  </svg>
);

// `refreshKey` (optional): bump it from a parent to force a re-fetch — e.g. after
// a profile photo upload awards points so member rows reflect the new total.
function useFetch(endpoint, token, groupToken, key, refreshKey) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/${endpoint}`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (active && res.data?.error === 0) setData(res.data[key] || []);
      })
      .catch(() => active && setData([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [endpoint, token, groupToken, key, refreshKey]);
  return { loading, data };
}

export function MembersView({ token, groupToken, primary, member, groupCard, refreshKey, chatHidden = false, chatLocked = false, chatLevelName = "" }) {
  const studio = useTemplate() === "studio";
  const { loading, data } = useFetch("community-members", token, groupToken, "members", refreshKey);
  const { me, pts } = useMyPoints(token, groupToken, member, refreshKey);
  const members = data || [];
  // member_token of the person the signed-in member is chatting with (opens the
  // chat modal straight into that thread); null = closed.
  const [chatTarget, setChatTarget] = useState(null);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
        {groupCard}
        <MyPointsCard me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
        <InlineBanner token={token} groupToken={groupToken} placement="member_page" wrapperClass="space-y-3" />
      </aside>

      {/* Members (card-based) — main column on the left on desktop */}
      <div className="lg:col-span-8 order-1">
        <div className={panelCls(studio)}>
          <SectionHeader
            primary={primary}
            iconPath="M17 20h5v-1a4 4 0 00-3-3.87M9 20H4v-1a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-2a3 3 0 10-2.5-4.5"
            title="Members"
            subtitle="Everyone in this group"
            trailing={loading ? "…" : `${members.length} ${members.length === 1 ? "member" : "members"}`}
          />
          {chatLocked && !chatHidden && (
            <div className="mx-4 mt-4 flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
              <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" />
              </svg>
              <span>
                {chatLevelName
                  ? `Reach the “${chatLevelName}” level to chat with members.`
                  : "Reach the required level to chat with members."}
              </span>
            </div>
          )}
          {loading ? (
            <PageLoader embedded label="Loading members…" primary={primary} />
          ) : members.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm">No members yet.</p>
          ) : (
            <ul className="p-4 space-y-2.5 bg-gray-50/40">
              {members.map((m, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3.5 bg-white rounded-xl border border-gray-100 shadow-sm p-3 hover:shadow-md hover:border-gray-200 transition"
                >
                  <span className="rounded-full p-[2px] shrink-0" style={{ background: `${primary}26` }}>
                    <span className="block rounded-full ring-2 ring-white">
                      <Avatar name={m.name} img={m.img} primary={primary} size={44} />
                    </span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-800 text-[15px] truncate flex items-center gap-1.5">
                      <span className="truncate">{m.name}</span>
                      {m.is_admin && (
                        <span
                          className="shrink-0 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: `${primary}1a`, color: primary }}
                        >
                          Admin
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                      </svg>
                      Joined {m.joined_on || "—"}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {!chatHidden && !chatLocked && member && m.member_token && m.member_token !== member.url_token && (
                      <button
                        type="button"
                        onClick={() => setChatTarget(m.member_token)}
                        title={`Chat with ${m.name}`}
                        aria-label={`Chat with ${m.name}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap text-white transition hover:brightness-110"
                        style={{ backgroundColor: primary }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 10.5h8M8 14h5m-9 6.5l3.8-2.2H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14.5z" />
                        </svg>
                        <span className="hidden sm:inline">Chat</span>
                      </button>
                    )}
                    <span
                      className="text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap"
                      style={{ backgroundColor: `${primary}14`, color: primary }}
                    >
                      {m.points} pts
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {chatTarget && (
        <ChatModal
          token={token}
          member={member}
          primary={primary}
          startWith={chatTarget}
          onClose={() => setChatTarget(null)}
        />
      )}
    </div>
  );
}

export function ReferralLink({ token, groupToken, urlToken, primary, points }) {
  const [copied, setCopied] = useState(false);
  // Share a deep link straight into this group's feed (guest-viewable) carrying
  // the referrer's code: /:token?gid={group}&page=feed&ref={urlToken}. Falls back
  // to the community home link when there's no group in context.
  const link = groupToken
    ? `${window.location.origin}/${token}?gid=${groupToken}&page=feed&ref=${urlToken}`
    : `${window.location.origin}/${token}?ref=${urlToken}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.createElement("textarea");
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="mt-5 pt-5 border-t border-gray-100">
      <div
        className="rounded-xl p-3.5"
        style={{ backgroundColor: `${primary}0d`, border: `1px solid ${primary}1f` }}
      >
        {/* Header — icon chip + title + reward badge */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0"
            style={{ backgroundColor: primary }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </span>
          <p className="text-sm font-bold text-gray-900">Referral Link</p>
          {points > 0 && (
            <span
              className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ backgroundColor: `${primary}1f`, color: primary }}
            >
              +{points} pts
            </span>
          )}
        </div>

        {/* Link + copy */}
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={link}
            onClick={(e) => e.target.select()}
            className="flex-1 min-w-0 text-xs bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-gray-600 outline-none focus:border-gray-300"
          />
          <button
            onClick={copy}
            title={copied ? "Copied!" : "Copy link"}
            aria-label="Copy referral link"
            style={{ backgroundColor: primary }}
            className="shrink-0 flex items-center gap-1.5 px-3 rounded-lg text-white text-xs font-semibold hover:opacity-90 transition active:scale-95"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <CopyIcon />
                Copy
              </>
            )}
          </button>
        </div>

        <p className="text-[11px] text-gray-500 mt-2.5 leading-relaxed">
          Earn{" "}
          <span className="font-semibold" style={{ color: primary }}>
            {points} {points === 1 ? "point" : "points"}
          </span>{" "}
          for every new sign up through your referral link.
        </p>
      </div>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// Ordered to match the "My Points" layout: each row reads "<activity> to get  N Points".
const POINT_LABELS = {
  post: "Make a post to get",
  comment: "Comment on a post to get",
  like: "Like a comment or post to get",
  profile_picture: "Upload Profile picture to get",
  lesson_complete: "Mark complete a lesson to get",
  viral_share: "Viral share of referral link",
};

// Per-activity glyph for the "How to earn" rows (single SVG path each).
const ACTIVITY_ICON = {
  post: "M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z",
  comment: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
  like: "M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3",
  profile_picture: "M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z",
  lesson_complete: "M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  viral_share: "M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13",
};

// Short call-to-action labels for the "how to earn points" chips on the hero.
const EARN_SHORT = {
  post: "Post",
  comment: "Comment",
  like: "Like",
  profile_picture: "Upload your profile picture",
  lesson_complete: "Complete a Lesson",
  viral_share: "Refer a friend",
};

// Gold / silver / bronze medal backgrounds for the top-3 leaderboard ranks.
const RANK_MEDAL_BG = {
  1: "linear-gradient(135deg, #FDE68A 0%, #F59E0B 100%)",
  2: "linear-gradient(135deg, #F1F5F9 0%, #94A3B8 100%)",
  3: "linear-gradient(135deg, #FED7AA 0%, #EA580C 100%)",
};


// Premium LIGHT section header — subtle brand-tinted background, brand-colored
// icon chip, a title + supporting subtitle, and a soft count pill. Used by the
// Members / Rewards panels for a cleaner, more modern hierarchy.
export function SectionHeader({ iconPath, title, subtitle, trailing, primary }) {
  const studio = useTemplate() === "studio";
  return (
    <div
      className={`flex items-center ${studio ? "gap-5 px-6 sm:px-8 py-5 sm:py-7" : "gap-4 px-5 sm:px-6 py-4 sm:py-5"}`}
      style={{
        background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
        borderBottom: `1px solid ${primary}1f`,
      }}
    >
      <span
        className={`flex items-center justify-center shrink-0 border bg-white ${studio ? "w-14 h-14 rounded-2xl shadow-sm" : "w-11 h-11 rounded-xl"}`}
        style={{ color: primary, borderColor: `${primary}29` }}
      >
        <svg className={studio ? "w-7 h-7" : "w-[22px] h-[22px]"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </span>
      <div className="min-w-0">
        <h2 className={`font-bold tracking-tight text-gray-900 leading-tight ${studio ? "text-2xl sm:text-[28px]" : "text-xl"}`}>{title}</h2>
        {subtitle && <p className={`text-gray-500 truncate ${studio ? "text-sm mt-1" : "text-[13px] mt-0.5"}`}>{subtitle}</p>}
      </div>
      {trailing != null && (
        <span
          className={`ml-auto shrink-0 font-bold rounded-full whitespace-nowrap ${studio ? "text-[13px] px-4 py-2" : "text-xs px-3 py-1.5"}`}
          style={{ backgroundColor: `${primary}14`, color: primary }}
        >
          {trailing}
        </span>
      )}
    </div>
  );
}

/**
 * Premium "My Points" card — gradient header with the member's identity, floating
 * rank/points stat tiles, an iconified "how to earn" list, and the referral link.
 * Shared by every page that shows points so the design stays consistent.
 */
// Friendly one-line description of the member's current level + what's next.
function levelBlurb(level, total) {
  if (!level) return "";
  if (level.is_max) return "You've reached the highest level — you're a top member of this group! 🏆";
  const n = level.level_no || 1;
  if (n === 1) return `You're just getting started — stay active to reach ${level.next_name} and unlock more.`;
  if (total && n >= total - 1) return `Almost at the top! Just ${level.points_to_next} more points to reach ${level.next_name}.`;
  return `Great progress! Keep posting and engaging to reach ${level.next_name}.`;
}

// Standalone "Levels" card — the member's level ladder. Rendered inside the
// My Points card on most pages, and on its own in the leaderboard rail (where
// the full My Points card is replaced by the "Your standing" hero).
export function LevelsCard({ me, primary }) {
  if (!me || !me.levels || me.levels.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 7-7M21 7v5m0-5h-5" />
            </svg>
          </span>
          <h3 className="font-bold text-[15px] tracking-wide text-gray-900">Levels</h3>
        </div>
        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
          {me.levels.filter((l) => l.achieved).length}/{me.levels.length} unlocked
        </span>
      </div>
      <ul className="space-y-2">
        {me.levels.map((lv) => {
          const done = lv.achieved;
          const cur = lv.is_current;
          return (
            <li
              key={lv.level_no}
              className="flex items-center gap-3 rounded-xl border p-2.5"
              style={{
                borderColor: cur ? primary : done ? `${primary}33` : "#f0f0f0",
                backgroundColor: cur ? `${primary}0f` : done ? `${primary}08` : "#fafafa",
              }}
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-extrabold shrink-0"
                style={
                  done
                    ? { backgroundColor: primary, color: "#fff" }
                    : { backgroundColor: "#fff", color: "#9ca3af", border: "1px solid #e5e7eb" }
                }
              >
                {done ? lv.level_no : <LockMini />}
              </span>

              <span className="flex flex-col min-w-0 flex-1 leading-tight">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`truncate text-sm font-bold ${done ? "text-gray-900" : "text-gray-400"}`}>{lv.name}</span>
                  {cur && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                      style={{ backgroundColor: `${primary}26`, color: primary }}
                    >
                      Current
                    </span>
                  )}
                </span>
                {done ? (
                  lv.achieved_at ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
                      <TrophyMini className="w-3 h-3" />
                      Reached {fmtAchieved(lv.achieved_at)}
                    </span>
                  ) : (
                    <span className="text-[11px] font-semibold text-gray-400">Completed</span>
                  )
                ) : (
                  <span className="text-[11px] font-medium text-gray-400">{lv.points_required} pts to unlock</span>
                )}
              </span>

              {done && (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function MyPointsCard({ me, pts = {}, token, groupToken, primary, member, roleName = "", specialist = "" }) {
  const earnRows = Object.keys(POINT_LABELS).filter((k) => (pts[k] || 0) > 0);
  const headerBg = `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`;
  // A member only holds a rank once they've earned at least 1 point — and
  // hidden staff (rank === null) are excluded from the leaderboard entirely.
  const hasPoints = !!me && Number(me.points) > 0;
  const ranked = hasPoints && me.rank != null;
  const showMedal = ranked && me.rank <= 3;
  const medalColor = me && (me.rank === 1 ? "#f59e0b" : me.rank === 2 ? "#9ca3af" : "#d97706");

  // Earned badges shown alongside points (only the ones this member has earned,
  // rendered small so the card height barely changes).
  const [earnedBadges, setEarnedBadges] = useState([]);
  const memberId = member?.community_member_id;
  useEffect(() => {
    if (!token || !memberId) {
      setEarnedBadges([]);
      return;
    }
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-rewards`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: memberId,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setEarnedBadges((res.data.badges || []).filter((b) => b.earned));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, memberId]);

  // Gamification is opt-in: if the community/group hasn't set any point values
  // (all earn actions + referral are 0), hide the whole Points & Badges box.
  const anyPointsConfigured = earnRows.length > 0 || Number(pts.viral_share || 0) > 0;
  if (!anyPointsConfigured) return null;

  return (
    <div className="space-y-4">
      {/* ============ Card 1 — My Points ============ */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Light theme header — title, member, and Rank/Points at the top */}
      <div className="px-4 pt-3.5 pb-4" style={{ background: headerBg, borderBottom: `1px solid ${primary}1f` }}>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
          </svg>
          <h3 className="font-bold text-[15px] tracking-wide text-gray-900">My Points &amp; Badges</h3>
        </div>

        {me ? (
          <>
            <div className="flex items-center gap-2.5 mt-3">
              <Avatar name={me.name} img={me.img} primary={primary} size={36} />
              <div className="min-w-0">
                <p className="font-bold truncate text-sm leading-tight text-gray-900 flex flex-wrap items-center gap-1">
                  <span className="truncate">{me.name}</span>
                  {roleName && (
                    <span
                      className="shrink-0 text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: `${primary}1a`, color: primary }}
                    >
                      {roleName}
                    </span>
                  )}
                  {specialist && (
                    <span
                      className="shrink-0 text-[9px] font-semibold tracking-wide px-1.5 py-0.5 rounded-full"
                      style={{ border: `1px solid ${primary}59`, color: primary }}
                    >
                      {specialist}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-gray-500">of {me.total} members</p>
              </div>
            </div>
            {/* Rank + Points */}
            <div className="grid grid-cols-2 gap-2.5 mt-3">
              <div className="rounded-xl bg-white border border-gray-100 shadow-sm py-2 text-center">
                <div className="text-xl font-extrabold leading-none flex items-center justify-center gap-1" style={{ color: hasPoints ? primary : "#9ca3af" }}>
                  {showMedal && <MedalIcon className="h-[18px] w-[18px] shrink-0" style={{ color: medalColor }} />}
                  {ranked ? `#${me.rank}` : "—"}
                </div>
                <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mt-1">Rank</div>
              </div>
              <div className="rounded-xl bg-white border border-gray-100 shadow-sm py-2 text-center">
                <div className="text-xl font-extrabold leading-none" style={{ color: primary }}>{me.points}</div>
                <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mt-1">Points</div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 mt-3">Sign in to see your rank and points.</p>
        )}
      </div>

      {/* Earned badges — only the badges this member has unlocked, kept small. */}
      {me && earnedBadges.length > 0 && (
        <div className="px-4 pt-3.5 pb-3.5">
          <div className="mb-2 flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" style={{ color: primary }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l2.39 4.84 5.34.78-3.86 3.77.91 5.32L12 14.98 7.22 16.5l.91-5.32L4.27 7.41l5.34-.78z" />
            </svg>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primary }}>
              Earned badges · {earnedBadges.length}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {earnedBadges.map((b) => (
              <span
                key={b.id}
                title={b.name}
                className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-1 ring-gray-100"
              >
                <img src={b.image || DEFAULT_BADGE_IMAGE} alt={b.name} className="h-full w-full object-contain p-0.5" />
              </span>
            ))}
          </div>
        </div>
      )}

      {me && me.level && (
        <div className="px-4 py-4">
            <div
              className="overflow-hidden rounded-2xl border"
              style={{ borderColor: `${primary}26`, background: `linear-gradient(135deg, ${primary}12 0%, ${primary}05 100%)` }}
            >
              {/* Level header — badge + name + position */}
              <div className="flex items-center gap-3 p-4 pb-3">
                <div
                  className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-md ring-1 ring-white/30"
                  style={{ background: `linear-gradient(160deg, ${primary} 0%, ${primary}cc 100%)` }}
                >
                  <span className="text-xl font-extrabold leading-none">{me.level.level_no}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primary }}>
                    Your level{me.levels?.length ? ` · ${me.level.level_no} of ${me.levels.length}` : ""}
                  </p>
                  <p className="truncate text-lg font-extrabold leading-tight text-gray-900">{me.level.level_name}</p>
                  {me.level.achieved_at && (
                    <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
                      <TrophyMini className="w-3 h-3" />
                      Reached {fmtAchieved(me.level.achieved_at)}
                    </p>
                  )}
                </div>
              </div>

              {/* Short description + progress to next level */}
              <div className="px-4 pb-4">
                <p className="text-[12.5px] leading-relaxed text-gray-600">
                  {levelBlurb(me.level, me.levels?.length)}
                </p>
                {!me.level.is_max ? (
                  <>
                    <div className="mt-2.5 flex items-center justify-between text-[11px] font-bold">
                      <span className="text-gray-400">{me.points} pts</span>
                      <span style={{ color: primary }}>{me.level.points_to_next} pts to {me.level.next_name}</span>
                    </div>
                    <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-white ring-1 ring-gray-100">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${me.level.progress_pct}%`, background: `linear-gradient(90deg, ${primary} 0%, ${primary}aa 100%)` }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold" style={{ backgroundColor: `${primary}1a`, color: primary }}>
                    🏆 Max level reached
                  </div>
                )}
              </div>
            </div>
        </div>
      )}
      </div>
      {/* /Card 1 — My Points */}

      {/* ============ Card 2 — Levels ============ */}
      <LevelsCard me={me} primary={primary} />
      {/* /Card 2 — Levels */}

      {/* ============ Card 3 — How to earn points ============ */}
      {me && earnRows.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            <h3 className="font-bold text-[15px] tracking-wide text-gray-900">How to earn points</h3>
          </div>
          <ul className="divide-y divide-gray-50">
                {earnRows.map((k) => (
                  <li key={k} className="flex items-center gap-3 py-2">
                    <span
                      className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${primary}12`, color: primary }}
                    >
                      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d={ACTIVITY_ICON[k]} />
                      </svg>
                    </span>
                    <span className="text-[13px] text-gray-600 flex-1 min-w-0">{POINT_LABELS[k]}</span>
                    <span
                      className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: `${primary}14`, color: primary }}
                    >
                      +{pts[k]} pts
                    </span>
                  </li>
                ))}
              </ul>
          {me.url_token && (pts.viral_share || 0) > 0 && (
            <ReferralLink token={token} groupToken={groupToken} urlToken={me.url_token} primary={primary} points={pts.viral_share} />
          )}
        </div>
      )}
      {/* /Card 3 — How to earn points */}
    </div>
  );
}

/** Fetch the signed-in member's standing (rank/points) + point settings. */
export function useMyPoints(token, groupToken, member, refreshKey) {
  const [me, setMe] = useState(null);
  const [pts, setPts] = useState({});
  const memberId = member?.community_member_id;
  // Exposed so callers can re-pull the standing right after a point-earning
  // action (post, comment, …) so "My Points" updates immediately, no refresh.
  // `refreshKey` lets a parent force the same re-pull (e.g. profile photo upload).
  const refresh = useCallback(() => {
    if (!token) return Promise.resolve();
    return axios
      .post(`${config.COMMUNITY_API_URL}/community-leaderboard`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: memberId || undefined,
      })
      .then((res) => {
        if (res.data?.error === 0) {
          setMe(res.data.me || null);
          setPts(res.data.points_settings || {});
        }
      })
      .catch(() => {});
    // `refreshKey` isn't read in the body — it's an intentional trigger: bumping
    // it from a parent gives `refresh` a new identity so the effect below re-pulls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, groupToken, memberId, refreshKey]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { me, pts, refresh };
}

// Celebratory "your standing" banner — the member's own rank, points, level and
// progress to the next level, front and centre so they feel their progress.
function YourStandingHero({ me, pts = {}, primary, token, groupToken, member }) {
  const lvl = me.level;
  // A member only holds a rank once they've earned at least 1 point. With 0
  // points we show a motivational "earn your first point" state instead.
  const hasPoints = Number(me.points) > 0;
  const showMedal = hasPoints && me.rank <= 3;
  const medalColor = me.rank === 1 ? "#f59e0b" : me.rank === 2 ? "#9ca3af" : "#d97706";
  const earnRows = Object.keys(EARN_SHORT).filter((k) => (pts[k] || 0) > 0);

  // Earned badges — same source as the My Points card (only the ones unlocked).
  const [earnedBadges, setEarnedBadges] = useState([]);
  const memberId = member?.community_member_id;
  useEffect(() => {
    if (!token || !memberId) {
      setEarnedBadges([]);
      return;
    }
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-rewards`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: memberId,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setEarnedBadges((res.data.badges || []).filter((b) => b.earned));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, memberId]);

  return (
    <div
      className="overflow-hidden rounded-2xl border p-5 shadow-sm sm:p-6"
      style={{
        background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
        borderColor: `${primary}26`,
      }}
    >
      <div className="flex items-center gap-4">
        <span className="shrink-0 rounded-full bg-white p-[3px] shadow-sm ring-1" style={{ "--tw-ring-color": `${primary}33` }}>
          <Avatar name={me.name} img={me.img} primary={primary} size={56} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>Your standing</p>
          {hasPoints ? (
            <p className="flex items-center gap-1.5 text-2xl font-extrabold leading-tight text-gray-900 sm:text-[26px]">
              {showMedal && <MedalIcon className="h-6 w-6 shrink-0" style={{ color: medalColor }} />}
              <span>You're #{me.rank} <span className="text-base font-semibold text-gray-400">of {me.total}</span></span>
            </p>
          ) : (
            <p className="text-2xl font-extrabold leading-tight text-gray-900 sm:text-[26px]">Not ranked yet</p>
          )}
          {lvl && (
            <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
              <span className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-extrabold text-white" style={{ backgroundColor: primary }}>{lvl.level_no}</span>
              {lvl.level_name}
            </span>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-3xl font-extrabold leading-none sm:text-4xl" style={{ color: primary }}>{me.points}</p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">points</p>
        </div>
      </div>
      {lvl && (
        <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">{levelBlurb(lvl, me.levels?.length)}</p>
      )}
      {!hasPoints && !lvl && (
        <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">
          Earn your first point to claim a spot on the leaderboard — every post, comment and like counts. Start now and climb the ranks!
        </p>
      )}
      {lvl && !lvl.is_max ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] font-bold">
            <span className="text-gray-500">Level {lvl.level_no} · {lvl.level_name}</span>
            <span style={{ color: primary }}>{lvl.points_to_next} pts to {lvl.next_name} →</span>
          </div>
          <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-white ring-1 ring-gray-100">
            <div className="h-full rounded-full" style={{ width: `${lvl.progress_pct}%`, background: `linear-gradient(90deg, ${primary} 0%, ${primary}aa 100%)` }} />
          </div>
        </div>
      ) : lvl && lvl.is_max ? (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold" style={{ backgroundColor: `${primary}1a`, color: primary }}>
          🏆 You've reached the top level — legend status!
        </div>
      ) : null}

      {/* Earned badges — the badges this member has unlocked, kept small. */}
      {earnedBadges.length > 0 && (
        <div className="mt-4 border-t pt-3" style={{ borderColor: `${primary}1f` }}>
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>
            Earned badges · {earnedBadges.length}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {earnedBadges.map((b) => (
              <span
                key={b.id}
                title={b.name}
                className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-1 ring-gray-100"
              >
                <img src={b.image || DEFAULT_BADGE_IMAGE} alt={b.name} className="h-full w-full object-contain p-0.5" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* What to do next to earn points */}
      {earnRows.length > 0 && (
        <div className="mt-4 border-t pt-3" style={{ borderColor: `${primary}1f` }}>
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>
            {lvl && !lvl.is_max
              ? `${lvl.points_to_next} pts to ${lvl.next_name} — here's how to earn them`
              : "Keep earning points — here's how"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {earnRows.map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-gray-600 shadow-sm ring-1 ring-gray-100"
              >
                <svg className="h-3.5 w-3.5" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d={ACTIVITY_ICON[k]} />
                </svg>
                {EARN_SHORT[k]}
                <b className="font-extrabold" style={{ color: primary }}>+{pts[k]}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Top-3 podium — gold centre (raised), silver left, bronze right.
function Podium({ top3, primary, meRank }) {
  const slots = [top3[1], top3[0], top3[2]].filter(Boolean); // 2nd · 1st · 3rd
  const meta = {
    1: { ped: "h-20 sm:h-24", av: 72 },
    2: { ped: "h-14 sm:h-16", av: 58 },
    3: { ped: "h-10 sm:h-12", av: 58 },
  };
  return (
    <div className="px-4 pb-1 pt-6 sm:px-6">
      <div className="grid grid-cols-3 items-end gap-2 sm:gap-4">
        {slots.map((m) => {
          const cfg = meta[m.rank];
          const isMe = m.rank === meRank;
          return (
            <div key={m.rank} className="flex min-w-0 flex-col items-center">
              {m.rank === 1 && (
                <svg className="mb-1 h-6 w-6 text-amber-400 drop-shadow" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 7l4.5 4L12 5l4.5 6L21 7l-1.6 9.3a1 1 0 01-1 .85H5.6a1 1 0 01-1-.85L3 7z" />
                </svg>
              )}
              <span className="rounded-full p-[3px] shadow-lg" style={{ background: RANK_MEDAL_BG[m.rank] }}>
                <span className="block rounded-full ring-2 ring-white">
                  <Avatar name={m.name} img={m.img} primary={primary} size={cfg.av} />
                </span>
              </span>
              <p className="mt-2 max-w-full truncate text-[13px] font-bold text-gray-900">
                {m.name}{isMe && <span className="ml-1 text-[10px] font-bold uppercase" style={{ color: primary }}>You</span>}
              </p>
              {m.level && <p className="max-w-full truncate text-[10px] font-semibold" style={{ color: primary }}>{m.level.level_name}</p>}
              <span className="mt-1 inline-flex items-baseline gap-1 rounded-full bg-gray-50 px-2 py-0.5 ring-1 ring-gray-100">
                <span className="text-[13px] font-extrabold tabular-nums" style={{ color: primary }}>{m.points}</span>
                <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">pts</span>
              </span>
              <div className={`mt-2 flex w-full ${cfg.ped} items-start justify-center rounded-t-xl pt-1.5`} style={{ background: RANK_MEDAL_BG[m.rank] }}>
                <span className="text-lg font-extrabold text-white drop-shadow sm:text-xl">{m.rank}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LeaderboardView({ token, groupToken, primary, member, groupCard, refreshKey }) {
  const studio = useTemplate() === "studio";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [pts, setPts] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/community-leaderboard`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setRows(res.data.leaderboard || []);
        setMe(res.data.me || null);
        setPts(res.data.points_settings || {});
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken, member?.community_member_id, refreshKey]);

  // Only members with at least 1 point appear on the leaderboard.
  const ranked = rows.filter((r) => Number(r.points) > 0);
  // Top 3 get a podium; ranks 4–15 render as themed gradient "pill bar" rows.
  const podium = ranked.length >= 3 ? ranked.slice(0, 3) : [];
  const listRows = ranked.length >= 3 ? ranked.slice(3, 15) : ranked.slice(0, 15);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
        {groupCard}
        {/* My Points lives in the "Your standing" hero at the top, so it's not
            repeated here — but the Levels ladder still belongs in the rail. */}
        <LevelsCard me={me} primary={primary} />
        <InlineBanner token={token} groupToken={groupToken} placement="leaderboard" wrapperClass="space-y-3" />
      </aside>

      {/* Your standing + leaderboard — main column on the left on desktop */}
      <div className="lg:col-span-8 order-1 space-y-5">
        {!loading && me && <YourStandingHero me={me} pts={pts} primary={primary} token={token} groupToken={groupToken} member={member} />}

        <div className={panelCls(studio)}>
          <SectionHeader
            primary={primary}
            iconPath="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3"
            title="Leaderboard"
            subtitle="Top members ranked by points"
            trailing={ranked.length > 0 ? `Top ${Math.min(ranked.length, 15)}` : null}
          />
          {loading ? (
            <PageLoader embedded label="Loading leaderboard…" primary={primary} />
          ) : ranked.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm">No ranked members yet.</p>
          ) : (
            <>
              {podium.length === 3 && <Podium top3={podium} primary={primary} meRank={me?.rank} />}
              {listRows.length > 0 && (
              <ul className="px-4 sm:px-5 pb-5 pt-4 space-y-3">
              {listRows.map((m) => {
                const top = m.rank <= 3;
                const isMe = me && m.rank === me.rank;
                return (
                  <li
                    key={m.rank}
                    className="group relative flex items-center gap-3 rounded-full py-3 pr-5 pl-[66px] sm:pl-[70px] transition-all duration-200 hover:-translate-y-0.5"
                    style={{
                      background: `linear-gradient(95deg, ${primary}${top ? "2b" : "14"} 0%, ${primary}0d 46%, ${primary}05 100%)`,
                      boxShadow: isMe
                        ? `0 8px 20px -8px ${primary}80, inset 0 0 0 1.5px ${primary}`
                        : `0 5px 14px -10px ${primary}99, inset 0 0 0 1px ${primary}1f`,
                    }}
                  >
                    {/* Glassy top sheen for depth */}
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/45 via-transparent to-transparent" />

                    {/* Avatar overlapping the rounded left end */}
                    <span className="absolute left-1 top-1/2 -translate-y-1/2">
                      <span
                        className="block rounded-full p-[3px] shadow-[0_3px_10px_rgba(0,0,0,0.16)]"
                        style={{ background: top ? RANK_MEDAL_BG[m.rank] : "#ffffff" }}
                      >
                        <span className="block rounded-full ring-2 ring-white">
                          <Avatar name={m.name} img={m.img} primary={primary} size={48} />
                        </span>
                      </span>
                    </span>

                    {/* Rank medallion (top 3) or muted number */}
                    {top ? (
                      <span
                        className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-extrabold text-white shadow-sm ring-1 ring-white/60 shrink-0"
                        style={{ background: RANK_MEDAL_BG[m.rank] }}
                      >
                        {m.rank}
                      </span>
                    ) : (
                      <span className="w-[26px] text-center text-[15px] font-extrabold text-gray-400/90 tabular-nums shrink-0">
                        {m.rank}
                      </span>
                    )}

                    {/* Name + level achievement (two-line premium cell) */}
                    <span className="relative flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                      <span className="flex items-center gap-1.5 min-w-0">
                        {m.rank === 1 && (
                          <svg
                            className="w-4 h-4 shrink-0"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                            style={{ color: primary }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l4.5 4L12 5l4.5 6L21 7l-1.6 9.3a1 1 0 01-1 .85H5.6a1 1 0 01-1-.85L3 7zM4.8 17h14.4" />
                          </svg>
                        )}
                        <span className={`${top ? "font-extrabold" : "font-bold"} text-gray-900 text-[15px] tracking-tight truncate`}>{m.name}</span>
                        {isMe && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                            style={{ backgroundColor: `${primary}26`, color: primary }}
                          >
                            You
                          </span>
                        )}
                      </span>

                      {/* Level badge + "reached" date */}
                      {m.level && (
                        <span className="flex items-center gap-1.5 min-w-0 text-[11px] leading-none">
                          <span
                            className="inline-flex items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 font-bold shrink-0"
                            style={{ backgroundColor: `${primary}14`, color: primary }}
                            title={`Level ${m.level.level_no}`}
                          >
                            <span
                              className="flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-extrabold text-white shrink-0"
                              style={{ backgroundColor: primary }}
                            >
                              {m.level.level_no}
                            </span>
                            <span className="truncate">{m.level.level_name}</span>
                          </span>
                          {m.level.achieved_at && (
                            <span className="hidden sm:inline-flex items-center gap-1 text-gray-400 whitespace-nowrap shrink-0">
                              <TrophyMini className="w-3 h-3" />
                              <span className="font-semibold">{fmtAchieved(m.level.achieved_at)}</span>
                            </span>
                          )}
                        </span>
                      )}
                    </span>

                    {/* Divider + points */}
                    <span className="relative h-6 w-px shrink-0" style={{ backgroundColor: `${primary}33` }} />
                    <span className="relative shrink-0 flex items-baseline gap-1 whitespace-nowrap pl-1">
                      <span className="text-[17px] font-extrabold tabular-nums leading-none" style={{ color: primary }}>{m.points}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">pts</span>
                    </span>
                  </li>
                );
              })}
            </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ e, primary, past, tz }) {
  const online = (e.event_type || 1) <= 3;
  const lo = toZonedEvent(e, tz);
  return (
    <div className={`flex flex-col sm:flex-row sm:items-stretch overflow-hidden rounded-[10px] border border-gray-200 bg-white shadow-sm transition hover:shadow-md ${past ? "opacity-75" : ""}`}>
      {/* Image — full-width banner on mobile, a left thumbnail on desktop. The
          brand gradient sits behind as the ultimate fallback if it fails. */}
      <div
        className="relative w-full h-40 shrink-0 sm:h-auto sm:w-48"
        style={{ background: `linear-gradient(135deg, ${primary}29 0%, ${primary}0d 100%)` }}
      >
        <img
          src={e.image || DEFAULT_EVENT_IMAGE}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(ev) => {
            ev.currentTarget.style.display = "none";
          }}
        />
      </div>

      {/* Middle — title, meta, description */}
      <div className="min-w-0 flex-1 p-4">
        <div className="mb-1 flex items-center gap-2">
          <span
            className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ backgroundColor: `${primary}14`, color: primary }}
          >
            {online ? "Online" : "In person"}
          </span>
          {past && <span className="text-[11px] font-medium text-gray-400">Past</span>}
        </div>
        <h3 className="text-[15px] font-bold text-gray-900 line-clamp-2 sm:truncate">{e.title}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500">
          <span className="flex items-center gap-1.5">
            <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
            </svg>
            {lo.dateLabel}{lo.timeLabel ? ` · ${lo.timeLabel}` : ""}
          </span>
          {e.location && (
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{e.location}</span>
            </span>
          )}
        </div>
        {e.description && <p className="mt-1.5 line-clamp-2 text-sm text-gray-500">{e.description}</p>}
      </div>

      {/* CTA — full-width below the details on mobile, right-aligned on desktop */}
      {(e.link && !past) || past ? (
        <div className="flex shrink-0 items-center px-4 pb-4 sm:py-4 sm:pl-2 sm:pr-4">
          {e.link && !past ? (
            <a
              href={e.link}
              target="_blank"
              rel="noreferrer"
              style={{ color: primary, borderColor: primary }}
              className="block w-full text-center sm:w-auto whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-bold uppercase tracking-wide transition hover:bg-gray-50 active:scale-[0.98]"
            >
              {online ? "Join" : "View"}
            </a>
          ) : (
            <span className="block w-full text-center sm:w-auto whitespace-nowrap rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-400">
              Ended
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const localIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtTime = (t) => (t ? t.toLowerCase().replace(/\s+/g, "") : "");

// Date (YYYY-MM-DD) of an instant within a given IANA timezone.
const isoInZone = (d, tz) => {
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" })
        .formatToParts(d)
        .map((x) => [x.type, x.value])
    );
    return `${p.year}-${p.month}-${p.day}`;
  } catch {
    return localIso(d);
  }
};

// Event times are stored in UTC. The API sends the UTC date (`date_iso`, Y-m-d)
// and UTC time (`time`, "h:mm AM/PM") plus the community `timezone`; we rebuild
// that exact instant and render it in the COMMUNITY timezone (same for every
// viewer). Pure frontend — Intl does the conversion, no server tz math.
function toZonedEvent(e, tz) {
  if (!e || !e.date_iso) {
    return { localIso: e?.date_iso || null, dateLabel: e?.date, timeLabel: e?.time || null, hasTime: !!e?.time, startMs: null };
  }
  let hh = 0;
  let mm = 0;
  let hasTime = false;
  if (e.time) {
    const m = String(e.time).match(/(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
    if (m) {
      hh = parseInt(m[1], 10);
      mm = parseInt(m[2], 10);
      const ap = (m[3] || "").toUpperCase();
      if (ap === "PM" && hh < 12) hh += 12;
      if (ap === "AM" && hh === 12) hh = 0;
      hasTime = true;
    }
  }
  const d = new Date(`${e.date_iso}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00Z`);
  if (Number.isNaN(d.getTime())) {
    return { localIso: e.date_iso, dateLabel: e.date, timeLabel: e.time || null, hasTime: !!e.time, startMs: null };
  }
  const opts = tz ? { timeZone: tz } : {};
  return {
    localIso: tz ? isoInZone(d, tz) : localIso(d),
    dateLabel: d.toLocaleDateString("en-US", { ...opts, month: "short", day: "2-digit", year: "numeric" }),
    timeLabel: hasTime ? d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" }) : null,
    hasTime,
    // True start instant (epoch ms) for the countdown timer; only meaningful
    // when the event has a time set.
    startMs: hasTime ? d.getTime() : null,
  };
}

// A single circular-progress ring with the value + label centred (reference
// design: light grey track + a coloured arc for the unit's progress).
function CountdownRing({ value, label, max, color, size = 62 }) {
  const stroke = size <= 46 ? 3 : 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, max ? value / max : 0));
  const offset = circ * (1 - frac);
  // Scale the inner text to the ring so it stays balanced and centred at any size.
  const numFs = Math.round(size * 0.3); // 42 → 13px, 62 → 19px
  const labFs = Math.max(6, Math.round(size * 0.16)); // 42 → 7px, 62 → 10px
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-extrabold leading-none text-gray-800 tabular-nums"
          style={{ fontSize: numFs }}
        >
          {String(value).padStart(2, "0")}
        </span>
        <span
          className="mt-px font-semibold leading-none tracking-tight text-gray-500"
          style={{ fontSize: labFs }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

// Premium event countdown — four circular progress rings (Days/Hours/Minutes/
// Seconds) that tick every second. Renders a "Live now" pill once started.
function EventCountdown({ startMs, accent = "#F59E0B", size = 62 }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startMs) return null;

  const diff = startMs - now;
  if (diff <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)` }}
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
        Live now
      </span>
    );
  }

  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const rings = [
    { value: days, label: "Days", max: 365, color: "#2EA8E6" },
    { value: hrs, label: "Hours", max: 24, color: "#FF5C4D" },
    { value: mins, label: "Minutes", max: 60, color: "#7E57C2" },
    { value: secs, label: "Seconds", max: 60, color: "#2EA8E6" },
  ];

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3">
      {rings.map((r) => (
        <CountdownRing key={r.label} value={r.value} label={r.label} max={r.max} color={r.color} size={size} />
      ))}
    </div>
  );
}

/** Skool-style event detail popup — opens when a calendar entry is clicked. */
function EventDetailModal({ e, primary, tz, onClose }) {
  if (!e) return null;
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const tzCity = zone.split("/").pop().replace(/_/g, " ");
  const lo = toZonedEvent(e, tz);
  const todayZ = tz ? isoInZone(new Date(), tz) : localIso(new Date());
  const isToday = lo.localIso && lo.localIso === todayZ;
  const dayLabel = isToday ? "Today" : lo.dateLabel;
  const online = (e.event_type || 1) <= 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header — event image, or a calendar glyph placeholder */}
        <div
          className="relative h-40 shrink-0 flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${primary}29 0%, ${primary}0d 100%)` }}
        >
          <img
            src={e.image || DEFAULT_EVENT_IMAGE}
            alt=""
            className="h-full w-full object-cover"
            onError={(ev) => {
              ev.currentTarget.style.display = "none";
            }}
          />
          <button
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow hover:bg-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          <h3 className="text-xl font-bold text-gray-900">{e.title}</h3>

          <div className="mt-3 flex items-start gap-2.5">
            <svg className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
            </svg>
            <div className="text-sm">
              <p className="font-semibold text-gray-900">
                {dayLabel}
                {lo.timeLabel ? ` @ ${fmtTime(lo.timeLabel)}` : ""}
              </p>
              {tzCity && <p className="text-gray-500">{tzCity} time</p>}
            </div>
          </div>

          {e.location && (
            <div className="mt-2.5 flex items-center gap-2.5 text-sm text-gray-600">
              <svg className="h-5 w-5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {e.location}
            </div>
          )}

          {e.link && (
            <div className="mt-2.5 flex items-center gap-2.5 text-sm">
              <svg className="h-5 w-5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
              </svg>
              <a href={e.link} target="_blank" rel="noreferrer" className="truncate font-medium hover:underline" style={{ color: primary }}>
                {e.link}
              </a>
            </div>
          )}

          {e.description && (
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">{e.description}</p>
          )}

        </div>

        {/* Pinned footer — CTA stays visible while the content scrolls above. */}
        {e.link && (
          <div className="shrink-0 border-t border-gray-100 p-4">
            <a
              href={e.link}
              target="_blank"
              rel="noreferrer"
              style={{ backgroundColor: primary }}
              className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold uppercase tracking-wide text-white hover:opacity-90 active:scale-[0.99] transition"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
              </svg>
              {(e.cta || "").trim() || (online ? "Join event" : "View event")}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Premium "next upcoming event" banner shown at the top of the feed, just below
 * the group hero. Renders only the single next event; clicking it (anywhere but
 * the CTA link) opens the full event detail popup. Nothing renders when there
 * is no upcoming event.
 */
export function UpcomingEventBanner({ token, groupToken, primary }) {
  const [event, setEvent] = useState(null);
  const [tz, setTz] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-events`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setTz(res.data.timezone || "");
        // API returns upcoming events ordered by date — take the next one.
        setEvent((res.data.upcoming || [])[0] || null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken]);

  if (!event) return null;

  const lo = toZonedEvent(event, tz);
  const online = (event.event_type || 1) <= 3;
  // Admin-configured CTA button text; falls back to the default action label.
  const ctaText = (event.cta || "").trim();
  // Fixed "alert" highlight for the event card so it stands out regardless of
  // the community's theme colour (amber = friendly "heads-up, upcoming event").
  const accent = "#F59E0B";

  // Big day number + month for the left-hand date chip.
  let day = "";
  let mon = "";
  if (lo.localIso) {
    const parts = lo.localIso.split("-");
    day = String(parseInt(parts[2], 10));
    mon = new Date(`${lo.localIso}T00:00:00`)
      .toLocaleDateString("en-US", { month: "short" })
      .toUpperCase();
  }

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(true)}
        className="group relative cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
        style={{ borderColor: `${accent}33`, background: `linear-gradient(115deg, ${accent}1f 0%, #ffffff 52%)` }}
      >
        {/* Alert accent rail */}
        <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: accent }} />
        {/* Soft decorative glow */}
        <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
        <div className="relative flex items-center gap-3 px-4 py-3.5 pl-5 sm:py-2">
          {/* Date chip — solid theme gradient */}
          <div
            className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg text-center text-white shadow ring-1 ring-white/30 sm:h-10 sm:w-10"
            style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}d9 100%)` }}
          >
            <span className="text-lg font-extrabold leading-none sm:text-base">{day || "—"}</span>
            {mon && <span className="text-[8px] font-bold tracking-[0.1em] opacity-90 sm:text-[7px]">{mon}</span>}
          </div>

          {/* Details — compact two-line layout */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              {/* Full event name on a single line (length is capped on the
                  backend create page, so it won't overflow). On mobile the
                  ONLINE badge moves to the meta row below so the title gets the
                  full width and never collides with the badge/CTA. */}
              <h3 className="min-w-0 whitespace-nowrap text-sm font-bold text-gray-900">{event.title}</h3>
              {online && (
                <span className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-600">
                  <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
                  </svg>
                  Online
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] font-medium text-gray-500">
              {/* Mobile-only ONLINE chip (desktop shows it beside the title). */}
              {online && (
                <span className="sm:hidden inline-flex items-center gap-1 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-600">
                  <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
                  </svg>
                  Online
                </span>
              )}
              <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wide" style={{ color: accent }}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: accent }} />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                </span>
                Upcoming
              </span>
              <span className="flex items-center gap-1">
                <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
                {lo.dateLabel}
                {lo.timeLabel ? ` · ${lo.timeLabel}` : ""}
              </span>
              {event.location && (
                <span className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{event.location}</span>
                </span>
              )}
            </div>
          </div>

          {/* Inline countdown — keeps the whole alert on one compact row */}
          {lo.startMs && (
            <div className="hidden shrink-0 items-center md:flex">
              <EventCountdown startMs={lo.startMs} accent={accent} size={42} />
            </div>
          )}

          {/* CTA */}
          <div className="hidden shrink-0 sm:flex items-center">
            {event.link ? (
              <a
                href={event.link}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundColor: accent, boxShadow: `0 6px 14px -8px ${accent}99` }}
                className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:opacity-95 active:scale-[0.98]"
              >
                {/* Event (calendar) icon — the banner represents an upcoming
                    event, so the action button shows the event icon. */}
                <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
                <span className="max-w-[8rem] truncate">{ctaText || (online ? "Join" : "View")}</span>
              </a>
            ) : (
              <span
                className="flex items-center gap-1 rounded-lg border px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide transition group-hover:bg-gray-50"
                style={{ color: accent, borderColor: `${accent}40` }}
              >
                Details
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            )}
          </div>

          {/* Mobile CTA — desktop hides the CTA/countdown above sm; give phones
              a visible action: a Join/View link button, or a details chevron.
              Aligned to the bottom so a long title can run across the cleared
              top-right space instead of being hidden behind the button. */}
          <div className="flex shrink-0 items-center self-end sm:hidden">
            {event.link ? (
              <a
                href={event.link}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                aria-label={ctaText || (online ? "Join event" : "View event")}
                style={{ backgroundColor: accent, boxShadow: `0 6px 14px -8px ${accent}99` }}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-white transition active:scale-95"
              >
                {/* Event (calendar) icon — the banner represents an upcoming
                    event, so the action button shows the event icon. */}
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
              </a>
            ) : (
              <span
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-lg border"
                style={{ color: accent, borderColor: `${accent}40` }}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            )}
          </div>
        </div>
      </div>

      {open && (
        <EventDetailModal e={event} primary={primary} tz={tz} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

export function EventsView({ token, groupToken, primary, member, groupCard }) {
  const studio = useTemplate() === "studio";
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [tz, setTz] = useState(""); // community timezone (from API)
  // Open on the grid/list of event cards by default; users switch to the
  // month calendar with the toggle.
  const [view, setView] = useState("list"); // "calendar" | "list"
  // Mobile: tapping a day with multiple events opens a compact day list.
  const [dayPopup, setDayPopup] = useState(null); // { label, events } | null
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selected, setSelected] = useState(null);
  const { me, pts } = useMyPoints(token, groupToken, member);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/community-events`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setTz(res.data.timezone || "");
        setEvents([...(res.data.upcoming || []), ...(res.data.past || [])]);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken]);

  // Events keyed by their date in the COMMUNITY timezone for cell lookup.
  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) {
      const key = toZonedEvent(e, tz).localIso;
      if (!key) continue;
      (m[key] ||= []).push(e);
    }
    return m;
  }, [events, tz]);

  const today = new Date();
  const todayIso = tz ? isoInZone(today, tz) : localIso(today);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const tzCity = zone.split("/").pop().replace(/_/g, " ");
  const nowLabel = fmtTime(today.toLocaleTimeString("en-US", { ...(tz ? { timeZone: tz } : {}), hour: "numeric", minute: "2-digit" }));

  // 6-week (or fewer) Monday-first grid for the visible month.
  const cells = useMemo(() => {
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = Math.ceil((startOffset + daysInMonth) / 7);
    return Array.from({ length: weeks * 7 }, (_, i) => new Date(year, month, 1 - startOffset + i));
  }, [year, month]);

  const upcoming = events.filter((e) => e.upcoming);
  const past = events.filter((e) => !e.upcoming);

  const ToggleBtn = ({ mode, label, d }) => {
    const active = view === mode;
    return (
      <button
        onClick={() => setView(mode)}
        aria-label={label}
        className="flex h-8 w-9 items-center justify-center transition"
        style={active ? { backgroundColor: `${primary}14`, color: primary } : { color: "#9ca3af" }}
      >
        <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={d} />
        </svg>
      </button>
    );
  };

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
      <button
        onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
        className="rounded-full border border-gray-200 px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        Today
      </button>

      {view === "calendar" ? (
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            aria-label="Previous month"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="text-center min-w-0 sm:min-w-[120px]">
            <div className="font-bold text-gray-900 leading-tight text-sm sm:text-base truncate">{monthLabel}</div>
            <div className="text-[10px] sm:text-[11px] text-gray-400 truncate">{nowLabel} {tzCity} time</div>
          </div>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            aria-label="Next month"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      ) : (
        <div />
      )}

      <div className="flex items-center overflow-hidden rounded-lg border border-gray-200">
        <ToggleBtn mode="list" label="List view" d="M4 6h16M4 12h16M4 18h16" />
        <ToggleBtn mode="calendar" label="Calendar view" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
      </div>
    </div>
  );

  const calendar = (
    <div className="overflow-x-auto">
      <div className="sm:min-w-[680px]">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/60">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-gray-500">
              {w}
            </div>
          ))}
        </div>
        {/* Day cells (1px grid via gap on a gray backdrop) */}
        <div className="grid grid-cols-7 gap-px bg-gray-100">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === month;
            const key = localIso(d);
            const dayEvents = byDate[key] || [];
            const isToday = key === todayIso;
            const openDay = () => {
              if (!dayEvents.length) return;
              if (dayEvents.length === 1) return setSelected(dayEvents[0]);
              setDayPopup({
                label: d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }),
                events: dayEvents,
              });
            };
            return (
              <div key={i} className={`min-h-[68px] sm:min-h-[112px] bg-white p-1 sm:p-1.5 ${inMonth ? "" : "bg-gray-50/40"}`}>
                <div className="mb-1 flex justify-center sm:justify-start">
                  <span
                    className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-xs font-semibold ${
                      isToday ? "text-white" : inMonth ? "text-gray-700" : "text-gray-300"
                    }`}
                    style={isToday ? { backgroundColor: primary } : undefined}
                  >
                    {d.getDate()}
                  </span>
                </div>

                {/* Desktop: full event labels */}
                <div className="hidden sm:block space-y-1">
                  {dayEvents.map((e) => {
                    const lo = toZonedEvent(e, tz);
                    return (
                      <button
                        key={e.id}
                        onClick={() => setSelected(e)}
                        title={e.title}
                        className={`block w-full truncate rounded px-1 py-0.5 text-left text-[11px] font-semibold hover:underline ${e.upcoming ? "" : "opacity-60"}`}
                        style={{ color: primary }}
                      >
                        {lo.timeLabel ? `${fmtTime(lo.timeLabel)} - ` : ""}{e.title}
                      </button>
                    );
                  })}
                </div>

                {/* Mobile: compact dots — tap opens the event / day list */}
                {dayEvents.length > 0 && (
                  <button
                    type="button"
                    onClick={openDay}
                    aria-label={`${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"} on ${d.getDate()}`}
                    className="sm:hidden mx-auto flex flex-wrap items-center justify-center gap-0.5 px-0.5"
                  >
                    {dayEvents.slice(0, 3).map((e) => (
                      <span key={e.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: primary, opacity: e.upcoming ? 1 : 0.5 }} />
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] font-bold leading-none" style={{ color: primary }}>+{dayEvents.length - 3}</span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const list =
    upcoming.length === 0 && past.length === 0 ? (
      <p className="py-12 text-center text-sm text-gray-400">No events scheduled yet.</p>
    ) : (
      <div className="p-4 sm:p-5 space-y-6">
        {upcoming.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-bold text-gray-900">Upcoming events <span className="text-gray-400">({upcoming.length})</span></h3>
            <div className="space-y-3">
              {upcoming.map((e) => (
                <EventCard key={e.id} e={e} primary={primary} tz={tz} />
              ))}
            </div>
          </div>
        )}
        {past.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-bold text-gray-900">Past events <span className="text-gray-400">({past.length})</span></h3>
            <div className="space-y-3">
              {past.map((e) => (
                <EventCard key={e.id} e={e} primary={primary} past tz={tz} />
              ))}
            </div>
          </div>
        )}
      </div>
    );

  const body = (
    <div className="space-y-5">
      {/* Members-style heading band at the top of the card. */}
      <div className={panelCls(studio)}>
        <SectionHeader
          primary={primary}
          iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          title="Events"
          subtitle="Upcoming & past events"
          trailing={loading ? "…" : `${events.length} ${events.length === 1 ? "event" : "events"}`}
        />
        {header}
        {loading ? (
          <PageLoader embedded label="Loading events…" primary={primary} />
        ) : view === "calendar" ? (
          calendar
        ) : (
          list
        )}
      </div>
    </div>
  );

  const wrapped = !groupCard ? (
    body
  ) : (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
        {groupCard}
        <MyPointsCard me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
        <InlineBanner token={token} groupToken={groupToken} placement="event_page" wrapperClass="space-y-3" />
      </aside>
      <div className="lg:col-span-8 order-1">{body}</div>
    </div>
  );

  return (
    <>
      {wrapped}
      <EventDetailModal e={selected} primary={primary} tz={tz} onClose={() => setSelected(null)} />

      {/* Mobile day-events list — shown when a calendar day has multiple events. */}
      {dayPopup && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setDayPopup(null)}>
          <div className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">{dayPopup.label}</h3>
              <button onClick={() => setDayPopup(null)} className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100" aria-label="Close">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto">
              {dayPopup.events.map((e) => {
                const lo = toZonedEvent(e, tz);
                return (
                  <button
                    key={e.id}
                    onClick={() => { setSelected(e); setDayPopup(null); }}
                    className={`w-full text-left rounded-xl border border-gray-200 px-3.5 py-3 hover:bg-gray-50 transition ${e.upcoming ? "" : "opacity-70"}`}
                  >
                    <div className="text-sm font-semibold text-gray-900 truncate">{e.title}</div>
                    {lo.timeLabel && <div className="text-xs text-gray-500 mt-0.5">{fmtTime(lo.timeLabel)}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Left-sidebar "My Badges" card — earned badges highlighted, locked ones dimmed. */
function MyBadgesCard({ badges = [], primary }) {
  const earnedCount = badges.filter((b) => b.earned).length;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Light theme header (matches My Points for a consistent rail) */}
      <div
        className="flex items-center gap-2 px-5 py-4"
        style={{ background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`, borderBottom: `1px solid ${primary}1f` }}
      >
        <svg className="w-4 h-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l7 4v6c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-4z" />
        </svg>
        <h3 className="font-bold text-[15px] tracking-wide text-gray-900">My Badges</h3>
        {badges.length > 0 && (
          <span
            className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: `${primary}14`, color: primary }}
          >
            {earnedCount}/{badges.length}
          </span>
        )}
      </div>
      {badges.length === 0 ? (
        <p className="text-center text-gray-400 py-10 text-sm">No badges yet.</p>
      ) : (
        <div className="p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {badges.map((b) => (
            <div
              key={b.id}
              title={b.earned ? b.name : `${b.name} — ${b.points} pts to unlock`}
              className="flex flex-col items-center text-center bg-gray-50/60 rounded-xl border border-gray-100 p-3"
            >
              <div className="relative">
                <span
                  className="block rounded-full p-[3px] shadow-sm"
                  style={{ background: b.earned ? RANK_MEDAL_BG[1] : "#e5e7eb" }}
                >
                  <span className="w-14 h-14 rounded-full bg-white ring-2 ring-white flex items-center justify-center overflow-hidden">
                    <img
                      src={b.image || DEFAULT_BADGE_IMAGE}
                      alt={b.name}
                      className={`w-full h-full object-contain p-1 ${b.earned ? "" : "grayscale opacity-50"}`}
                    />
                  </span>
                </span>
                <span
                  className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white ring-2 ring-white ${
                    b.earned ? "bg-green-500" : "bg-gray-400"
                  }`}
                >
                  {b.earned ? (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
                    </svg>
                  )}
                </span>
              </div>
              <p className="mt-2 text-xs font-semibold text-gray-700 truncate w-full">{b.name}</p>
              <span
                className="mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                style={
                  b.earned
                    ? { backgroundColor: "#dcfce7", color: "#16a34a" }
                    : { backgroundColor: `${primary}14`, color: primary }
                }
              >
                {b.earned ? "Earned" : `${b.points} pts`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Rewards-page "My Points" — same premium card as everywhere else (kept as a thin
    alias so the design stays consistent across all pages). */
function MyPointsPanel(props) {
  return <MyPointsCard {...props} />;
}

/** Normalise a video URL into something embeddable. */
function toEmbed(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return { kind: "video", src: url };
  return { kind: "iframe", src: url };
}

// Activity-type labels shown on the reward popup / card.
// resource_type: 1 = Instruction, 2 = External URL, 3 = Video, 4 = Audio, 5 = Upload File.
const REWARD_TYPE_LABEL = { 1: "Instruction", 2: "External URL", 3: "Video", 4: "Audio", 5: "Image" };

/** Render an unlocked reward's value according to its type. */
function RewardValue({ type, value, primary }) {
  if (!value) return <p className="text-sm text-gray-400">No content available for this reward.</p>;
  if (type === 1) {
    return <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{value}</div>;
  }
  if (type === 3) {
    const e = toEmbed(value);
    return e.kind === "video" ? (
      <video src={e.src} controls className="w-full rounded-xl bg-black" />
    ) : (
      <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingTop: "56.25%" }}>
        <iframe
          src={e.src}
          title="reward video"
          className="absolute inset-0 w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (type === 4) return <audio src={value} controls className="w-full" />;
  // type 2 (external URL) / type 5 (hosted file)
  return (
    <a
      href={value}
      target="_blank"
      rel="noreferrer"
      style={{ backgroundColor: primary }}
      className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 transition"
    >
      {type === 5 ? "Download" : "Open link"} →
    </a>
  );
}

/** Popup shown when a member clicks a reward's access button. */
function RewardModal({ reward, primary, onClose }) {
  useEffect(() => {
    if (!reward) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [reward, onClose]);

  if (!reward) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-gray-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl ring-1 ring-black/5 max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Premium header — reward name + type, on a brand gradient. */}
        <div
          className="relative px-6 py-5 overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}
        >
          <span className="pointer-events-none absolute -top-12 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <span className="pointer-events-none absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-black/10 blur-2xl" />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white text-lg leading-none transition"
          >
            ×
          </button>
          <div className="relative z-10 flex items-center gap-3.5 pr-8">
            {/* reward image or type glyph */}
            <span className="h-14 w-14 shrink-0 rounded-2xl overflow-hidden bg-white/15 ring-1 ring-white/25 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <img src={reward.image || DEFAULT_REWARD_IMAGE} alt="" className="h-full w-full object-cover" />
            </span>
            <div className="min-w-0">
              <h3 className="text-white text-xl font-bold leading-tight truncate">{reward.name || "Reward"}</h3>
              <p className="text-white/85 text-xs mt-1 flex items-center gap-1.5">
                <span>{REWARD_TYPE_LABEL[reward.type] || "Reward"}</span>
                <span className="text-white/40">•</span>
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l2.39 6.96H22l-6.2 4.51 2.37 7.03L12 16.9 5.83 20.5l2.37-7.03L2 8.96h7.61z" />
                  </svg>
                  {reward.points} {reward.points === 1 ? "point" : "points"}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Body — the reward value (earned) or the unlock prompt. */}
        <div className="p-5 sm:p-6">
          {reward.earned ? (
            <RewardValue type={reward.type} value={reward.value} primary={primary} />
          ) : (
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6 text-center">
              <span className="mx-auto mb-3 h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primary}14`, color: primary }}>
                <LockIcon className="w-5 h-5" />
              </span>
              <p className="text-sm text-gray-600">
                Reach{" "}
                <span className="font-semibold" style={{ color: primary }}>
                  {reward.points} points
                </span>{" "}
                to unlock this reward.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LockIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Type-aware glyph used inside the premium default reward banner. */
function RewardGlyph({ type, className = "w-8 h-8" }) {
  const common = { className, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (type === 3) {
    // video
    return (
      <svg {...common}>
        <rect x="2" y="5" width="14" height="14" rx="3" />
        <path d="M16 9l6-3v12l-6-3z" />
      </svg>
    );
  }
  if (type === 4) {
    // audio
    return (
      <svg {...common}>
        <path d="M9 18V6l10-2v12" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="16" cy="16" r="3" />
      </svg>
    );
  }
  // gift (instructions / external / file / default)
  return (
    <svg {...common}>
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
      <path d="M12 8C12 8 11 3 8 3a2.5 2.5 0 0 0 0 5h4zM12 8s1-5 4-5a2.5 2.5 0 0 1 0 5h-4z" />
    </svg>
  );
}

/**
 * Premium default artwork shown when a reward has no uploaded image: a branded
 * gradient in the community's primary colour with a soft glow, faint decorative
 * rings and a frosted-glass icon badge. Scales cleanly and stays on-brand.
 */
function RewardDefaultBanner({ primary, type }) {
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${primary} 0%, #0f172a 100%)` }}
    >
      {/* soft brand-coloured glow */}
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(120% 90% at 25% 15%, ${primary}80, transparent 55%)` }}
      />
      {/* faint decorative rings */}
      <svg className="absolute -right-10 -bottom-12 w-48 h-48 text-white/10" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="100" cy="100" r="40" />
        <circle cx="100" cy="100" r="70" />
        <circle cx="100" cy="100" r="96" />
      </svg>
      {/* frosted-glass icon badge */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-16 w-16 rounded-2xl bg-white/10 ring-1 ring-white/25 backdrop-blur-sm shadow-lg flex items-center justify-center">
          <RewardGlyph type={type} className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );
}

/**
 * A single reward, styled to match the community group cards: banner with corner
 * badges → logo chip + title → 3-line description → "N Points • Unlocked/Locked"
 * meta footer. The whole card is the action target (like the button-less group
 * cards): earned rewards open the value popup. Locked rewards keep their full
 * colour and, on hover, reveal a full-card black overlay with the unlock message.
 */
function RewardCard({ reward: r, primary, onOpen }) {
  const locked = !r.earned;

  const open = () => !locked && onOpen(r);

  return (
    <div className="relative group">
      <div
        onClick={open}
        role="button"
        tabIndex={0}
        aria-disabled={locked}
        onKeyDown={(e) => {
          if (!locked && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            open();
          }
        }}
        className={`relative bg-white rounded-2xl border border-gray-200 ring-1 ring-black/[0.03] shadow-sm overflow-hidden transition flex flex-col h-full ${
          locked ? "cursor-default" : "cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
        }`}
      >
        {/* compact banner */}
        <div className="h-40 bg-gray-100 overflow-hidden relative">
          {r.image ? (
            <img src={r.image} alt={r.name} className="w-full h-full object-contain" />
          ) : (
            <RewardDefaultBanner primary={primary} type={r.type} />
          )}
          {/* status badge — Locked (solid dark) vs Unlocked (light pill) */}
          {locked ? (
            <span className="absolute top-1.5 right-1.5 text-[10px] font-semibold text-white bg-gray-900/80 backdrop-blur px-1.5 py-0.5 rounded-full flex items-center gap-1">
              <LockIcon className="w-2.5 h-2.5" />
              Locked
            </span>
          ) : (
            <span
              className="absolute top-1.5 right-1.5 text-[10px] font-semibold bg-white/95 backdrop-blur px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
              style={{ color: primary }}
            >
              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 018 0M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
              </svg>
              Unlocked
            </span>
          )}
        </div>

        {/* body */}
        <div className="px-3 pb-3 pt-3 flex flex-col flex-1">
          <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{r.name}</h3>

          {/* description — reserve 2 lines so cards line up */}
          <p className="text-xs text-gray-500 mt-1 line-clamp-2 min-h-[2rem]">{r.description || "—"}</p>

          {/* bottom row: points pill + compact claim button */}
          <div className="mt-auto pt-2.5 flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 shrink-0"
              style={{ backgroundColor: `${primary}14`, color: primary }}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l2.39 6.96H22l-6.2 4.51 2.37 7.03L12 16.9 5.83 20.5l2.37-7.03L2 8.96h7.61z" />
              </svg>
              {r.points}
            </span>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              disabled={locked}
              style={locked ? undefined : { backgroundColor: primary }}
              className={`inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition active:scale-[0.97] truncate ${
                locked ? "bg-gray-100 text-gray-400 cursor-default" : "text-white hover:opacity-90"
              }`}
            >
              {locked && <LockIcon className="w-3 h-3" />}
              {locked ? "Locked" : r.btn_name || "Claim"}
            </button>
          </div>
        </div>

        {/* Full-card hover overlay — only for locked rewards. */}
        {locked && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-4 text-center opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-white text-xs font-semibold leading-relaxed">
              Reach {r.points} points to unlock this reward.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function RewardsView({ token, groupToken, primary, member, groupCard, refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [activeReward, setActiveReward] = useState(null);
  const { me, pts } = useMyPoints(token, groupToken, member, refreshKey);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/community-rewards`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setBadges(res.data.badges || []);
        setRewards(res.data.rewards || []);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken, member?.community_member_id, refreshKey]);

  // Earned-reward action: External URL opens the link, Upload File downloads,
  // everything else (Instruction / Video / Audio) opens the value popup.
  const handleReward = (r) => {
    if (!r.earned) return;
    if (Number(r.type) === 2) {
      if (r.value) window.open(r.value, "_blank", "noopener,noreferrer");
      return;
    }
    if (Number(r.type) === 5) {
      if (r.value) {
        const a = document.createElement("a");
        a.href = r.value;
        a.download = "";
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      return;
    }
    setActiveReward(r);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
        {groupCard}
        <MyPointsPanel me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
        <InlineBanner token={token} groupToken={groupToken} placement="reward_page" wrapperClass="space-y-3" />
      </aside>

      {/* Rewards + My Badges — main column on the left on desktop */}
      <div className="lg:col-span-8 order-1 space-y-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <SectionHeader
            primary={primary}
            iconPath="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z"
            title="Rewards"
            subtitle="Unlock perks as you earn points"
            trailing={!loading && rewards.length > 0 ? `${rewards.length} total` : null}
          />
          {loading ? (
            <PageLoader embedded label="Loading rewards…" primary={primary} />
          ) : rewards.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-gray-400">
              <svg className="h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z" />
              </svg>
              <p className="text-sm font-medium">No rewards configured yet.</p>
            </div>
          ) : (
            <div className="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {rewards.map((r) => (
                <RewardCard key={r.id} reward={r} primary={primary} onOpen={handleReward} />
              ))}
            </div>
          )}
        </div>

        {/* My Badges — below the rewards collection in the main column */}
        <MyBadgesCard badges={badges} primary={primary} />
      </div>

      <RewardModal reward={activeReward} primary={primary} onClose={() => setActiveReward(null)} />
    </div>
  );
}
